/**
 * Hook to fetch available periods from Management API
 * Uses the new GET /diffs endpoint that returns all periods in one call
 */

import { useQuery } from '@tanstack/react-query';

// API configuration
const API_BASE = import.meta.env.VITE_PRICINGSAAS_MANAGEMENT_API_URL ||
                 'https://nyqqynbtcrqdmhezmhsp.supabase.co/functions/v1/management';
const API_KEY = import.meta.env.VITE_PRICINGSAAS_MANAGEMENT_API_KEY;

interface DiffsStatsResponse {
  total_companies: number;
  total_diffs: number;
  earliest_version: string;
  latest_version: string;
  quarters_count: number;
  weeks_count: number;
  months_count: number;
  years_count: number;
  days_count: number;
  available_periods: {
    quarterly: string[];
    weekly: string[];
    monthly: string[];
    yearly: string[];
    daily: string[];
  };
}

/**
 * Fetch all available periods from the GET /diffs endpoint
 */
async function fetchAllPeriods(): Promise<string[]> {
  console.log('[fetchAllPeriods] Fetching from /diffs endpoint...');

  // include_unpublished=true so periods with only unpublished/QA-pending diffs
  // (e.g. the current review week) still appear in the picker.
  const response = await fetch(`${API_BASE}/diffs?include_unpublished=true`, {
    method: 'GET',
    headers: {
      'x-api-key': API_KEY,
      'Content-Type': 'application/json'
    }
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch periods: ${response.status} ${response.statusText}`);
  }

  const data: DiffsStatsResponse = await response.json();

  console.log('[fetchAllPeriods] Response:', {
    total_diffs: data.total_diffs,
    quarters: data.quarters_count,
    weeks: data.weeks_count,
    months: data.months_count,
    years: data.years_count
  });

  // Combine all available periods (daily first — most recent granularity)
  const allPeriods = [
    ...(data.available_periods.daily || []),
    ...data.available_periods.quarterly,
    ...data.available_periods.weekly,
    ...data.available_periods.monthly,
    ...data.available_periods.yearly
  ];

  console.log('[fetchAllPeriods] Combined periods:', allPeriods.length);
  return allPeriods;
}

/**
 * Hook to fetch all available periods from Management API
 * Uses the new GET /diffs endpoint that returns all periods
 */
export function useManagementPeriods() {
  console.log('[useManagementPeriods] Hook called');

  return useQuery({
    queryKey: ['managementPeriods'],
    queryFn: async (): Promise<string[]> => {
      console.log('[useManagementPeriods] Starting to fetch periods...');
      try {
        // Fetch all periods from the new /diffs endpoint
        const allPeriods = await fetchAllPeriods();

        // Periods are already sorted in the API response (newest first)
        // Just return them as-is
        console.log('[useManagementPeriods] Returning periods:', allPeriods.length, 'first 10:', allPeriods.slice(0, 10));
        return allPeriods;
      } catch (error) {
        console.error('[useManagementPeriods] Error fetching periods:', error);
        throw error;
      }
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    retry: 2,
  });
}

